import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

import OnboardingProgressBar from '../../components/onboarding/OnboardingProgressBar';
import WelcomeStep      from './steps/WelcomeStep';
import ComfortZoneStep  from './steps/ComfortZoneStep';
import ActivitiesStep   from './steps/ActivitiesStep';
import NudgePrefsStep   from './steps/NudgePrefsStep';
import CompleteStep     from './steps/CompleteStep';

import {
  saveComfortZones,
  addActivity,
  completeOnboarding,
} from '../../services/resetPath.service';
import { updateNudgePreferences } from '../../services/nudge.service';

// ─── Step definitions ─────────────────────────────────────────────────────────

const STEPS = [
  { key: 'welcome',      label: 'Welcome' },
  { key: 'comfort',      label: 'Comfort zones' },
  { key: 'activities',   label: 'Activities' },
  { key: 'nudges',       label: 'Nudges' },
  { key: 'complete',     label: 'Done' },
];

// Step indices
const STEP = {
  WELCOME:    0,
  COMFORT:    1,
  ACTIVITIES: 2,
  NUDGES:     3,
  COMPLETE:   4,
};

// ─── Main Onboarding component ────────────────────────────────────────────────

export default function Onboarding() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [step, setStep]           = useState(STEP.WELCOME);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  // Accumulated data across steps
  const [comfortZones, setComfortZones]   = useState(null);  // saved comfort zones from API
  const [savedActivities, setSavedActivities] = useState([]); // activities saved to API
  const [nudgePrefs, setNudgePrefs]       = useState(null);

  // If member is already active (re-visiting onboarding), redirect to dashboard
  useEffect(() => {
    if (user?.onboarding_phase === 'active') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // ─── Step handlers ──────────────────────────────────────────────────────────

  const handleWelcomeNext = () => {
    setError('');
    setStep(STEP.COMFORT);
  };

  const handleComfortNext = async (zonesData) => {
    setError('');
    setSaving(true);
    try {
      const saved = await saveComfortZones(zonesData);
      setComfortZones(saved);
      setStep(STEP.ACTIVITIES);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save comfort zones. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleActivitiesNext = async (activities) => {
    setError('');
    setSaving(true);
    try {
      // Save all activities to API in parallel
      const saved = await Promise.all(
        activities.map(a =>
          addActivity({
            sector:        a.sector,
            activity_name: a.activity_name,
            frequency:     a.frequency,
            target_count:  a.target_count,
          })
        )
      );
      setSavedActivities(saved);
      setStep(STEP.NUDGES);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save activities. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleNudgesNext = async (prefs) => {
    setError('');
    setSaving(true);
    try {
      const savedPrefs = await updateNudgePreferences(prefs);
      setNudgePrefs(savedPrefs);

      // Mark onboarding complete
      await completeOnboarding();

      // Update user in context so ProtectedRoute + routing see the new phase
      updateUser({ onboarding_phase: 'active' });

      setStep(STEP.COMPLETE);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  const showProgressBar = step !== STEP.COMPLETE;

  return (
    <div className="min-h-dvh bg-reset-cream">
      {/* Header */}
      <div className="border-b border-orange-100 bg-white/70 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center justify-between px-4 py-3 max-w-lg mx-auto">
          <span className="text-sm font-bold text-reset-terracotta">365reset</span>
          {step > STEP.WELCOME && step < STEP.COMPLETE && (
            <span className="text-xs text-gray-400">
              Step {step} of {STEPS.length - 1}
            </span>
          )}
        </div>
        {showProgressBar && (
          <OnboardingProgressBar currentStep={step} steps={STEPS} />
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="max-w-lg mx-auto mt-4 mx-4">
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mx-4">
            {error}
          </div>
        </div>
      )}

      {/* Saving overlay */}
      {saving && (
        <div className="fixed inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-lg px-8 py-6 text-center">
            <div className="animate-spin text-3xl mb-3">⟳</div>
            <p className="text-sm text-gray-600">Saving your reset path…</p>
          </div>
        </div>
      )}

      {/* Step content */}
      <main className="pb-12">
        {step === STEP.WELCOME && (
          <WelcomeStep onNext={handleWelcomeNext} />
        )}

        {step === STEP.COMFORT && (
          <ComfortZoneStep
            onNext={handleComfortNext}
            onBack={() => setStep(STEP.WELCOME)}
            initialValues={comfortZones}
          />
        )}

        {step === STEP.ACTIVITIES && (
          <ActivitiesStep
            onNext={handleActivitiesNext}
            onBack={() => setStep(STEP.COMFORT)}
            initialActivities={[]}
          />
        )}

        {step === STEP.NUDGES && (
          <NudgePrefsStep
            onNext={handleNudgesNext}
            onBack={() => setStep(STEP.ACTIVITIES)}
            initialPrefs={nudgePrefs}
          />
        )}

        {step === STEP.COMPLETE && (
          <CompleteStep
            resetPath={comfortZones}
            activities={savedActivities}
          />
        )}
      </main>
    </div>
  );
}
